import type { OfferKind } from './offer-marker';

/**
 * Dev-only structured recorder for assistant turns, consumed by the local
 * eval probe harness (`apps/reveliolabs-dashboard-pw-e2e/assistant-evals/`).
 *
 * Every hook is a no-op outside development builds, so nothing here can leak
 * into production behaviour or timing. The recorder publishes to
 * `window.__gaEval` — the probe runner polls that to know when a turn ended
 * and to assert on the tool sequence, digests, and offer-marker integrity
 * that the DOM alone cannot expose (a downgraded marker and intentional plain
 * text render identically).
 *
 * Records are intentionally append-only and bounded (MAX_TURNS) so a long
 * manual session doesn't grow memory unbounded.
 */

export type EvalToolCallRecord = {
  name: string;
  /** JSON-safe copy of the tool input (best effort — never throws). */
  input: unknown;
  startedAtMs: number;
  endedAtMs?: number;
  endsTurn?: boolean;
  /** True when the tool THREW (distinct from an ERROR: resultForModel). */
  threw?: boolean;
  /** True when resultForModel starts with "ERROR:". */
  errored?: boolean;
  /** First 600 chars of resultForModel — enough for figure/contract asserts. */
  resultPreview?: string;
};

export type EvalDigestRecord = {
  /** Which tool produced it: get_data_answer | apply_filters | render_chart. */
  source: string;
  metric: string;
  latestValue?: number;
  rowCount?: number;
  empty?: boolean;
  error?: boolean;
  resolvedRoleLabel?: string;
};

export type EvalMarkerDowngrade = {
  messageId: string;
  kind: OfferKind;
  phrase: string;
};

export type EvalTurnRecord = {
  prompt: string;
  startedAtMs: number;
  endedAtMs?: number;
  status?: 'done' | 'error' | 'aborted';
  toolCalls: EvalToolCallRecord[];
  digests: EvalDigestRecord[];
  markerDowngrades: EvalMarkerDowngrade[];
};

type EvalStore = { version: 1; turns: EvalTurnRecord[] };

declare global {
  interface Window {
    __gaEval?: EvalStore;
  }
}

const MAX_TURNS = 50;

const enabled = (): boolean =>
  process.env.NODE_ENV === 'development' && typeof window !== 'undefined';

const store = (): EvalStore => {
  const w = window;
  if (!w.__gaEval) {
    w.__gaEval = { version: 1, turns: [] };
  }
  return w.__gaEval;
};

const currentTurn = (): EvalTurnRecord | undefined => {
  const turns = store().turns;
  const last = turns[turns.length - 1];
  return last && last.endedAtMs === undefined ? last : undefined;
};

/** Best-effort JSON-safe copy; never throws on cycles/bigints/etc. */
const safeCopy = (value: unknown): unknown => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return String(value);
  }
};

export function evalRecordTurnStart(prompt: string): void {
  if (!enabled()) return;
  const s = store();
  // A turn superseded mid-flight (abort + new prompt) may never reach its
  // finally in order — close any dangling record so `currentTurn` can't
  // attribute the new turn's events to it.
  const dangling = currentTurn();
  if (dangling) {
    dangling.endedAtMs = Date.now();
    dangling.status = 'aborted';
  }
  s.turns.push({
    prompt,
    startedAtMs: Date.now(),
    toolCalls: [],
    digests: [],
    markerDowngrades: [],
  });
  if (s.turns.length > MAX_TURNS) {
    s.turns.splice(0, s.turns.length - MAX_TURNS);
  }
}

export function evalRecordTurnEnd(status: 'done' | 'error' | 'aborted'): void {
  if (!enabled()) return;
  const turn = currentTurn();
  if (!turn) return;
  turn.endedAtMs = Date.now();
  turn.status = status;
}

export function evalRecordToolCall(
  name: string,
  input: unknown
): EvalToolCallRecord | undefined {
  if (!enabled()) return undefined;
  const turn = currentTurn();
  if (!turn) return undefined;
  const record: EvalToolCallRecord = {
    name,
    input: safeCopy(input),
    startedAtMs: Date.now(),
  };
  turn.toolCalls.push(record);
  return record;
}

export function evalRecordToolResult(
  record: EvalToolCallRecord | undefined,
  result: { resultForModel: string; endsTurn?: boolean }
): void {
  if (!enabled() || !record) return;
  record.endedAtMs = Date.now();
  record.endsTurn = result.endsTurn === true;
  record.errored = result.resultForModel.startsWith('ERROR:');
  record.resultPreview = result.resultForModel.slice(0, 600);
}

export function evalRecordToolThrow(
  record: EvalToolCallRecord | undefined
): void {
  if (!enabled() || !record) return;
  record.endedAtMs = Date.now();
  record.threw = true;
}

export function evalRecordDigest(
  source: string,
  metric: string,
  digest: {
    latestValue?: number;
    rowCount?: number;
    empty?: boolean;
    error?: boolean;
    resolvedRoleLabel?: string;
  }
): void {
  if (!enabled()) return;
  const turn = currentTurn();
  if (!turn) return;
  turn.digests.push({
    source,
    metric,
    ...(digest.latestValue !== undefined
      ? { latestValue: digest.latestValue }
      : {}),
    ...(digest.rowCount !== undefined ? { rowCount: digest.rowCount } : {}),
    ...(digest.empty !== undefined ? { empty: digest.empty } : {}),
    ...(digest.error !== undefined ? { error: digest.error } : {}),
    ...(digest.resolvedRoleLabel !== undefined
      ? { resolvedRoleLabel: digest.resolvedRoleLabel }
      : {}),
  });
}

// Render-phase hook — deduped so re-renders don't multiply records. A
// downgrade means a narration carried an offer marker whose backing action
// could not render (missing/mismatched stamp, no access, unresolved target):
// the exact "no link AND no button" dead-end the harness exists to catch.
const seenDowngrades = new Set<string>();

export function evalRecordMarkerDowngrade(
  messageId: string,
  kind: OfferKind,
  phrase: string
): void {
  if (!enabled()) return;
  const key = `${messageId}|${kind}|${phrase}`;
  if (seenDowngrades.has(key)) return;
  seenDowngrades.add(key);
  // Attribute to the open turn when there is one; a downgrade discovered on a
  // later re-render (turn already closed) still lands on the LAST turn, which
  // is the one that produced the message in every real flow.
  const turns = store().turns;
  const turn = currentTurn() ?? turns[turns.length - 1];
  if (!turn) return;
  turn.markerDowngrades.push({ messageId, kind, phrase });
  console.warn(
    '[ga-eval] offer marker downgraded to plain text (no backing action):',
    kind,
    phrase
  );
}
